import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { ConectarApiService } from "@sharedV11/services/api/connection/conectar-api.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-edit-centro-coste-tx",
  templateUrl: "./edit-centro-coste-tx.component.html",
  styleUrls: ["./edit-centro-coste-tx.component.scss"],
})
export class EditCentroCosteTxComponent implements OnInit, OnDestroy {
  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  loading = true;
  translation: any;
  editRegistroForm: FormGroup;
  private destroy$ = new Subject<void>();
  private originalValues: any;

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    private httpErrorService: HttpErrorsService,
    private snackvar_service: SnackbarInfoService,
    private panelService: PanelService,
    private servicioAPI: ConectarApiService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.translation = this.translator.GetTranslations();
    this.initializeForm();
    this.registerDirtyWatcher();
    this.manageForm(this.pkId, this.mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Secuencias prohibidas por el backend (no usar bloque para evitar que "*/" cierre el comentario)
  private static readonly CARACTERES_PROHIBIDOS = [";", "/*", "*/", "@@", "!!", "--", "==", "//", "\\\\"];

  private static forbiddenCharsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || typeof control.value !== "string") return null;
      const v = control.value;
      const encontrado = EditCentroCosteTxComponent.CARACTERES_PROHIBIDOS.find((seq) => v.includes(seq));
      return encontrado ? { forbiddenChars: { value: encontrado } } : null;
    };
  }

  /** Elimina del texto las secuencias prohibidas (bloqueo en input como en Temporadas Tarifas). */
  static sanitizarCaracteresProhibidos(valor: string): string {
    let s = (valor ?? "").toString();
    EditCentroCosteTxComponent.CARACTERES_PROHIBIDOS.forEach((seq) => {
      s = s.split(seq).join("");
    });
    return s;
  }

  /** Bloquea en el campo las secuencias prohibidas: las elimina al escribir o pegar. */
  onInputCaracteresProhibidos(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const valor = input.value ?? "";
    const sanitizado = EditCentroCosteTxComponent.sanitizarCaracteresProhibidos(valor);
    if (sanitizado !== valor) {
      const ctrl = this.editRegistroForm.get(controlName);
      if (ctrl) {
        ctrl.setValue(sanitizado, { emitEvent: true });
        input.value = sanitizado;
      }
    }
  }

  initializeForm() {
    const noPermitidos = EditCentroCosteTxComponent.forbiddenCharsValidator();
    this.editRegistroForm = this.fb.group({
      pkId: [0],
      Clave: ["", [Validators.required, Validators.maxLength(50), noPermitidos]],
      Nombre: ["", [Validators.required, Validators.maxLength(50), noPermitidos]],
      UrlCentro: ["", [Validators.required, Validators.maxLength(255), noPermitidos]],
    });
  }

  private registerDirtyWatcher() {
    if (!this.editRegistroForm) return;
    this.editRegistroForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.panelService.setDiscardChanges(false));
  }

  async manageForm(pkId: string, mode: string) {
    if (pkId && mode !== "I") {
      const paginator = {
        page: 1,
        pagelements: 1,
        orderby: "pkId",
        ordertype: "ASC",
      };
      const filters = [{ name: "pkId", value: pkId }];

      this.servicioAPI
        .getCentrosCosteTXAsync(paginator, filters)
        .then((data) => {
          const lista = data?.DatosResult?.Lista ?? data?.DatosResult?.ListaCentros ?? [];
          if (lista.length) {
            const item = lista[0];
            const isDuplicate = mode === "D";
            const nombre = EditCentroCosteTxComponent.sanitizarCaracteresProhibidos((item.CentroCosteTX ?? item.Nombre ?? "").toString());
            const clave = EditCentroCosteTxComponent.sanitizarCaracteresProhibidos((item.CodigoCentroCosteTX ?? item.Clave ?? "").toString());
            const descripcion = EditCentroCosteTxComponent.sanitizarCaracteresProhibidos((item.DescripcionCentroCosteTX ?? item.UrlCentro ?? item.Idiomas ?? "").toString());
            this.editRegistroForm.patchValue({
              pkId: isDuplicate ? 0 : +(item.pkId || 0),
              Clave: clave,
              Nombre: nombre,
              UrlCentro: descripcion,
            });
            if (isDuplicate) this.mode = "I";
          }
          this.originalValues = { ...this.editRegistroForm.value };
          this.panelService.setDiscardChanges(true);
          this.loading = false;
        })
        .catch((error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        });
    } else {
      this.editRegistroForm.patchValue({
        pkId: 0,
        Clave: "",
        Nombre: "",
        UrlCentro: "",
      });
      this.originalValues = { ...this.editRegistroForm.value };
      this.loading = false;
      this.panelService.setDiscardChanges(true);
    }
  }

  discard() {
    if (this.editRegistroForm && this.originalValues) {
      this.editRegistroForm.reset(this.originalValues);
      this.panelService.setDiscardChanges(true);
    }
  }

  submit() {
    this.editRegistroForm.markAllAsTouched();
    if (this.editRegistroForm.invalid) return;

    const value = this.editRegistroForm.value;
    const body = {
      pkId: value.pkId || 0,
      Nombre: EditCentroCosteTxComponent.sanitizarCaracteresProhibidos(value.Nombre ?? ""),
      Clave: EditCentroCosteTxComponent.sanitizarCaracteresProhibidos(value.Clave || ""),
      UrlCentro: EditCentroCosteTxComponent.sanitizarCaracteresProhibidos(value.UrlCentro || ""),
    };

    this.loading = true;
    this.servicioAPI
      .saveCentroCosteTXAsync(body)
      .then((response) => {
        const hasErrors = !response?.DatosResult && response?.Mensajes?.length;

        if (hasErrors) {
          response.Mensajes.forEach((m) => {
            const codeKey = `CodErr_${m.CodigoMensaje}`;
            const translated = this.translation?.[codeKey] || m.DescripcionMensaje;
            this.snackvar_service.openSnackBar(
              translated,
              "",
              "red-snackbar",
              m.CodigoMensaje
            );
          });
          this.loading = false;
          return;
        }

        if (response?.DatosResult) {
          if (response?.Mensajes?.length) {
            response.Mensajes.forEach((m) => {
              const codeKey = `CodErr_${m.CodigoMensaje}`;
              const translated = this.translation?.[codeKey] || m.DescripcionMensaje;
              this.snackvar_service.openSnackBar(
                translated,
                "",
                "red-snackbar",
                m.CodigoMensaje
              );
            });
          } else {
            this.snackvar_service.openSnackBar(
              this.translation["MensajeSuccessSnackBar"] ||
                this.translation["GuardadoCorrectamente"] ||
                "Guardado correctamente",
              "",
              "green-snackbar"
            );
          }
          this.closePanel();
        } else {
          this.loading = false;
        }
      })
      .catch((error) => {
        this.httpErrorService.identificarErrores(error);
        this.loading = false;
      });
  }

  getErrorMessage(field: string) {
    const control = this.editRegistroForm.get(field);
    if (!control?.errors) return "";
    if (control.hasError("required")) return this.translation?.CampoRequerido || "Campo requerido";
    if (control.hasError("maxlength")) {
      const m = control.getError("maxlength");
      return (this.translation?.LongitudMaxima || "Longitud máxima") + ": " + (m?.requiredLength || "");
    }
    if (control.hasError("forbiddenChars")) {
      const err = control.getError("forbiddenChars");
      const char = err?.value ?? "";
      const msg = this.translation?.CaracteresNoPermitidos || "Caracteres o combinación no permitida por el backend";
      return char ? `${msg}: «${char}»` : msg;
    }
    return "Error";
  }

  validateFields(field: string) {
    const control = this.editRegistroForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /** Classe do contador de caracteres: cor normal, amarelo perto do limite (230+), vermelho ao completar (255). */
  getDescripcionCounterClass(): string {
    const len = (this.editRegistroForm?.get("UrlCentro")?.value?.length || 0) as number;
    if (len >= 255) return "counter-danger";
    if (len >= 230) return "counter-warning";
    return "counter-normal";
  }

  closePanel() {
    this.loading = false;
    this.panelService.setSavePanel(null);
    this.panelService.setDiscardChanges(true);
    this.panelService.setClosePanel(null);
  }
}
